import React, { useState, useEffect, useRef, useCallback } from "react";
import { Nav, Navbar as BootstrapNavbar } from "react-bootstrap";
import { motion } from "framer-motion";
import "../App.css";

const Navbar = () => {
  const [activeSection, setActiveSection] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const rafRef = useRef(null);
  const lastScrollY = useRef(0);

  const handleScroll = useCallback(() => {
    // Skip if scroll position hasn't changed significantly
    const currentScrollY = window.scrollY;
    if (Math.abs(currentScrollY - lastScrollY.current) < 10) {
      return;
    }
    lastScrollY.current = currentScrollY;

    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const sections = ["home", "about", "education", "projects"];
      const scrollPosition = currentScrollY + 100;

      // Change navbar appearance on scroll
      setScrolled(currentScrollY > 50);

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    // Use passive listener for better scroll performance
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Call once on mount
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  const handleBrandClick = () => {
    smoothScrollTo(0);
    setActiveSection("home");
  };

  const handleNavLinkClick = (id) => {
    const yOffset = -70;
    const element = document.getElementById(id);
    const y =
      element.getBoundingClientRect().top + window.pageYOffset + yOffset;

    smoothScrollTo(y);
    setActiveSection(id);
  };

  // Custom smooth scroll function for better performance
  const smoothScrollTo = (targetPosition) => {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 800;
    let start = null;

    const animation = (currentTime) => {
      if (start === null) start = currentTime;
      const timeElapsed = currentTime - start;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function for smooth animation
      const ease = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  return (
    <BootstrapNavbar className={`navbar-custom ${scrolled ? "scrolled" : ""}`} expand="lg">
      <div className="navbar-brand-container">
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <BootstrapNavbar.Brand
            onClick={handleBrandClick}
            className="nav-brand-custom"
          >
            <span className="brand-icon">&lt;</span>
            Anbu Krishnan
            <span className="brand-icon">/&gt;</span>
          </BootstrapNavbar.Brand>
        </motion.div>
      </div>
      <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" className="navbar-toggler-custom" />
      <BootstrapNavbar.Collapse id="basic-navbar-nav">
        <Nav className="ms-auto navbar-nav-custom">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Nav.Link
              onClick={() => handleNavLinkClick("about")}
              className={`nav-link-custom ${activeSection === "about" ? "active" : ""}`}
            >
              <span className="nav-number">01.</span> About
            </Nav.Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Nav.Link
              onClick={() => handleNavLinkClick("education")}
              className={`nav-link-custom ${activeSection === "education" ? "active" : ""}`}
            >
              <span className="nav-number">02.</span> Education
            </Nav.Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Nav.Link
              onClick={() => handleNavLinkClick("projects")}
              className={`nav-link-custom ${activeSection === "projects" ? "active" : ""}`}
            >
              <span className="nav-number">03.</span> Projects
            </Nav.Link>
          </motion.div>
        </Nav>
      </BootstrapNavbar.Collapse>
    </BootstrapNavbar>
  );
};

export default Navbar;
